import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  FormArray,
  FormBuilder,
} from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProductsService } from '../../services/products.service';
import { EventEmitterService } from '../../services/event-emitter.service';
import { debounceTime, Subject, switchMap, tap } from 'rxjs';
import { BusinessesService } from '../../services/businesses.service';
import { AuthenticationService } from '../../services/authentication.service';
import { AccountTypes } from '../../models/account_types';

@Component({
  standalone: false,
  selector: 'app-product-popup',
  templateUrl: './product-popup.component.html',
  styleUrl: './product-popup.component.scss',
})
export class ProductPopupComponent implements OnInit, AfterViewInit {
  user;
  title;
  type = '';
  formGroup: FormGroup;
  optionFormGroups: FormGroup[] = [];
  businesses = [];
  selectedBusiness = null;
  product;
  ingredients: string[] = [];
  @ViewChild('ingredientInput') ingredientInput: ElementRef;
  optionTypes = ['select', 'multi-select', 'number'];
  disableButton = false;
  accountTypes = AccountTypes;

  searchBusinesses = new Subject<string>();
  searchBusinessesNoResults = 'no-results-found';

  constructor(
    public bsModalRef: BsModalRef,
    private productsService: ProductsService,
    private authenticationService: AuthenticationService,
    private businessesService: BusinessesService,
    private eventEmitterService: EventEmitterService,
    private fb: FormBuilder,
  ) {}

  async ngOnInit() {
    await this.getUser();
    if (this.product && this.product.ingredients) {
      try {
        // If it's already an array, use it directly
        if (Array.isArray(this.product.ingredients)) {
          this.ingredients = this.product.ingredients;
        }
        // If it's a string, handle different formats
        else if (typeof this.product.ingredients === 'string') {
          const ingredientsStr = this.product.ingredients;

          // Handle single quotes by replacing them with double quotes
          if (ingredientsStr.includes("'")) {
            // Replace single quotes with double quotes, but only for quotes surrounding strings
            const fixedStr = ingredientsStr
              .replace(/\['/g, '["')
              .replace(/'\]/g, '"]')
              .replace(/', '/g, '", "')
              .replace(/':/g, '":')
              .replace(/: '/g, ': "')
              .replace(/',/g, '",');

            this.ingredients = JSON.parse(fixedStr);
          } else {
            // Standard JSON parsing for properly formatted strings
            this.ingredients = JSON.parse(ingredientsStr);
          }
        }
      } catch (error) {
        console.error('Error parsing ingredients:', error);
        // Fallback to empty array if parsing fails
        this.ingredients = [];
      }
    }

    if (this.product && this.product.options) {
      try {
        if (Array.isArray(this.product.options)) {
          this.product.options = this.product.options;
        } else if (typeof this.product.options === 'string') {
          this.product.options = JSON.parse(this.product.options);
        }

        // Convert choices to FormGroup objects for each option
        if (Array.isArray(this.product.options)) {
          this.product.options.forEach((option) => {
            if (option.choices && Array.isArray(option.choices)) {
              option.choices = option.choices.map((choice) => {
                return new FormGroup({
                  name: new FormControl(choice.name || '', Validators.required),
                  price: this.createPriceControl(
                    choice.price || 0,
                    Validators.required,
                  ),
                });
              });
            }
          });
        }
      } catch (error) {
        console.error('Error parsing options:', error);
        // Fallback to empty array if parsing fails
        this.product.options = [];
      }
    }

    this.getAllBusinesses('');
    this.setupForm();
  }

  ngAfterViewInit(): void {
    this.setupBusinessesSearch();
  }

  async getUser() {
    this.user = await this.authenticationService.getUserFromToken();
  }

  stripLeadingZero(value: string): string {
    const stripped = value.replace(/^0+(?=\d)/, '');
    return stripped === '' ? '0' : stripped;
  }

  createPriceControl(initialValue: any, validators: any = []): FormControl {
    const control = new FormControl(initialValue, validators);
    control.valueChanges.subscribe((value) => {
      if (typeof value !== 'string' || !value) {
        return;
      }
      const formatted = this.stripLeadingZero(value);
      if (formatted !== value) {
        control.setValue(formatted, { emitEvent: false });
      }
    });
    return control;
  }

  setupForm() {
    let own_product = 'external-product';
    if (this.user.user.business.type == AccountTypes.Merchant) {
      own_product = 'my-product';
    }
    if (
      this.product &&
      this.product.business_id == this.user.user.business.id
    ) {
      own_product = 'my-product';
    }

    this.formGroup = new FormGroup({
      name: new FormControl(
        this.product ? this.product.name : '',
        Validators.required,
      ),
      description: new FormControl(
        this.product ? this.product.description : '',
        Validators.required,
      ),
      has_options: new FormControl(
        this.product ? this.product.has_options == 'true' : false,
        Validators.required,
      ),
      options: new FormControl(
        this.product && this.product.options ? this.product.options : [],
      ),
      price: this.createPriceControl(
        this.product ? this.product.price : 0,
        Validators.required,
      ),
      published: new FormControl(
        this.product ? this.product.published == 'true' : true,
        Validators.required,
      ),
      own_product: new FormControl(own_product, Validators.required),
      business_id: new FormControl(
        this.product ? this.product.business_id : null,
      ),
      category: new FormControl(
        this.product ? this.product.category : '',
        Validators.required,
      ),
      imageURL: new FormControl(this.product ? this.product.imageURL : ''),
      file: new FormControl(null),
      ingredient: new FormControl(''),
    });

    this.createOptionFormGroups();
    this.updateOptionsValidators(this.formGroup.get('has_options').value);

    // Listen for changes to the options selection
    this.formGroup.get('options').valueChanges.subscribe((options) => {
      this.createOptionFormGroups();
    });

    // Listen for changes to has_options to update validators
    this.formGroup.get('has_options').valueChanges.subscribe((hasOptions) => {
      this.updateOptionsValidators(hasOptions);
    });

    this.formGroup
      .get('own_product')
      .valueChanges.subscribe((value) =>
        this.updateExternalProductValidators(value),
      );
    this.updateExternalProductValidators(own_product);
  }

  updateExternalProductValidators(ownProduct: string) {
    const businessControl = this.formGroup.get('business_id');
    if (ownProduct === 'external-product') {
      businessControl.setValidators(Validators.required);
    } else {
      businessControl.clearValidators();
    }
    businessControl.updateValueAndValidity();
  }

  createOptionFormGroups() {
    this.optionFormGroups = [];
    const selectedOptions = this.formGroup.get('options').value || [];

    selectedOptions.forEach((option) => {
      // Ensure choices are FormGroup objects
      if (option.choices && Array.isArray(option.choices)) {
        option.choices = option.choices.map((choice) => {
          // If it's already a FormGroup, keep it as is
          if (choice instanceof FormGroup) {
            return choice;
          }
          // If it's a plain object, convert to FormGroup
          return new FormGroup({
            name: new FormControl(choice.name || '', Validators.required),
            price: this.createPriceControl(
              choice.price || 0,
              Validators.required,
            ),
          });
        });
      }

      const optionForm = new FormGroup({
        name: new FormControl(option.name || '', Validators.required),
        type: new FormControl(option.type || 'select', Validators.required),
        required: new FormControl(
          option.required || false,
          Validators.required,
        ),
        choices: new FormControl(option.choices || []),
        price: this.createPriceControl(option.price || 0),
      });

      // Set initial validators based on type
      this.updateOptionValidators(optionForm, option.type);

      // Subscribe to type changes to update validators dynamically
      optionForm.get('type').valueChanges.subscribe((type) => {
        this.updateOptionValidators(optionForm, type);
      });

      // Subscribe to changes to update the original option object
      optionForm.valueChanges.subscribe((value) => {
        Object.assign(option, value);
      });

      this.optionFormGroups.push(optionForm);
    });
  }

  updateOptionsValidators(hasOptions: boolean) {
    const optionsControl = this.formGroup.get('options');
    if (hasOptions) {
      optionsControl.setValidators(Validators.required);
    } else {
      optionsControl.clearValidators();
    }
    optionsControl.updateValueAndValidity();
  }

  updateOptionValidators(optionForm: FormGroup, type: string) {
    const choicesControl = optionForm.get('choices');
    const priceControl = optionForm.get('price');

    if (type === 'number') {
      // For number type: price is required, choices is not
      priceControl.setValidators(Validators.required);
      choicesControl.clearValidators();
      optionForm.get('choices').setValue([]);
    } else if (type === 'select' || type === 'multi-select') {
      // For select/multi-select: price is not required, but we'll validate choices manually
      choicesControl.clearValidators();
      priceControl.clearValidators();
      optionForm.get('price').setValue(0);
    }

    // Update validity after changing validators
    choicesControl.updateValueAndValidity();
    priceControl.updateValueAndValidity();
  }

  addOption() {
    let options = this.formGroup.get('options').value;
    options.push({
      name: '',
      type: 'select',
      required: false,
      choices: [],
      price: 0,
    });
    this.formGroup.get('options').setValue(options);
  }

  deleteOption(index: number) {
    let options = this.formGroup.get('options').value;
    options.splice(index, 1);
    this.formGroup.get('options').setValue(options);
  }

  setOptionType(event, optionForm: FormGroup) {
    optionForm.get('type').setValue(event.value);
    this.updateOptionValidators(optionForm, event.value);
  }

  addChoice(optionForm: FormGroup) {
    optionForm.get('choices').value.push(
      new FormGroup({
        name: new FormControl('', Validators.required),
        price: this.createPriceControl(0, Validators.required),
      }),
    );
  }

  deleteChoice(optionForm: FormGroup, choiceIndex: number) {
    optionForm.get('choices').value.splice(choiceIndex, 1);
  }

  getAllBusinesses(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.businessesService.getAllBusinesses(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchBusinessesNoResults = 'no-results-found';
        this.businesses = response.items;

        if (this.product && this.product.business_id) {
          this.selectedBusiness = this.businesses.find(
            (tp) => tp.id == this.product.business_id,
          );
        }
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchBusinessesNoResults = 'no-results-found';
      },
    );
  }

  setupBusinessesSearch() {
    this.searchBusinesses
      .pipe(
        tap(() => (this.searchBusinessesNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllBusinesses(searchText)),
      )
      .subscribe();
  }

  setBusiness(event) {
    this.selectedBusiness = event.value;
    this.formGroup
      .get('business_id')
      .setValue(event.value ? event.value.id : null);
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  addIngredient(event: Event) {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    const ingredient = this.formGroup.get('ingredient').value?.trim();
    if (ingredient && !this.ingredients.includes(ingredient)) {
      this.ingredients.push(ingredient);
      this.formGroup.get('ingredient').setValue('');
    }
  }

  removeIngredient(index: number) {
    this.ingredients.splice(index, 1);
  }

  buttonClicked() {
    let formInvalid = false;
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      formInvalid = true;
    }

    // check also if options and their choices are valid
    if (this.formGroup.get('has_options').value) {
      this.optionFormGroups.forEach((optionForm) => {
        optionForm.markAllAsTouched();

        if (optionForm.invalid) {
          formInvalid = true;
        }

        // Manual validation for select/multi-select options that need choices
        const optionType = optionForm.get('type').value;
        if (optionType === 'select' || optionType === 'multi-select') {
          const choices = optionForm.get('choices').value;

          // Check if we have at least one choice
          if (!choices || !Array.isArray(choices) || choices.length === 0) {
            formInvalid = true;
          } else {
            // Check each choice FormGroup
            choices.forEach((choice) => {
              if (choice instanceof FormGroup) {
                choice.markAllAsTouched();

                if (choice.invalid) {
                  formInvalid = true;
                }
              }
            });
          }
        } else {
          // For number type options, validate choices are not required but validate other choices if they exist
          const choices = optionForm.get('choices').value;
          if (choices && Array.isArray(choices)) {
            choices.forEach((choice) => {
              if (choice instanceof FormGroup) {
                choice.markAllAsTouched();

                if (choice.invalid) {
                  formInvalid = true;
                }
              }
            });
          }
        }
      });
    }

    if (formInvalid) {
      return;
    }

    if (this.formGroup.value.own_product == 'my-product') {
      this.formGroup.value.business_id = this.user.user.business.id;
    }

    const formData = new FormData();

    let imageURL = JSON.stringify(this.formGroup.value.imageURL);
    imageURL = JSON.parse(imageURL);

    if (this.isBase64(imageURL)) {
      imageURL = '';
    }

    Object.keys(this.formGroup.value).forEach((key) => {
      if (key === 'imageURL') {
        formData.append(key, imageURL);
      } else if (key !== 'ingredient' && key !== 'options') {
        formData.append(key, this.formGroup.value[key]);
      }
    });

    // Add ingredients array to form data
    formData.append('ingredients', JSON.stringify(this.ingredients));

    // Process options to extract values from FormGroup objects
    let options = [];
    if (
      this.formGroup.value.options &&
      Array.isArray(this.formGroup.value.options)
    ) {
      options = this.formGroup.value.options.map((option) => {
        const processedOption = {
          name: option.name,
          type: option.type,
          required: option.required,
          price: option.price,
          choices: [],
        };

        if (option.choices && Array.isArray(option.choices)) {
          processedOption.choices = option.choices.map((choice) => {
            // If choice is a FormGroup object, extract its value
            if (choice.value) {
              return choice.value;
            } else {
              return choice;
            }
          });
        }

        return processedOption;
      });
    }

    // Always send options data, empty array if has_options is false
    if (this.formGroup.value.has_options) {
      formData.append('options', JSON.stringify(options));
    } else {
      formData.append('options', JSON.stringify([]));
    }

    if (this.disableButton == true) {
      return;
    }
    this.disableButton = true;

    if (this.type === 'new-product') {
      this.productsService.createProduct(formData).subscribe(
        (response) => {
          console.log('response:', response);
          this.eventEmitterService.callAllProducts();
          this.closeModal();
          this.disableButton = false;
        },
        (error) => {
          console.log('error:', error);
          this.disableButton = false;
        },
      );
    }
    if (this.type === 'edit-product') {
      this.productsService.updateProduct(this.product.id, formData).subscribe(
        (response) => {
          console.log('response:', response);
          this.eventEmitterService.callAllProducts();
          this.closeModal();
          this.disableButton = false;
        },
        (error) => {
          console.log('error:', error);
          this.disableButton = false;
        },
      );
    }
  }

  isBase64(dataUrl: string): boolean {
    if (!dataUrl) {
      return false;
    }
    const base64Regex = /^data:(.*?);base64,(.*)$/;
    const match = dataUrl.match(base64Regex);

    if (!match) {
      return false;
    }

    const base64String = match[2];
    const base64Pattern = /^[A-Za-z0-9+/]+={0,2}$/;

    return base64Pattern.test(base64String) && base64String.length % 4 === 0;
  }
}
